/**
 * Recherche pour le picker de fiche liée (WKF-013 §5.4).
 *
 * Réutilise l'endpoint `/search/global` (SEARCH-001) et le MÊME `queryKey` que la
 * palette ⌘K — le cache React Query est donc partagé. Gating RBAC identique : on
 * ne requête que les types dont l'utilisateur détient la permission de lecture.
 * Version allégée (pas d'aperçu, pas de « meilleur résultat » ni de groupes
 * calculés) : le picker regroupe lui-même.
 */

import { useMemo } from 'react'
import { keepPreviousData, useQuery } from '@tanstack/react-query'

import { useAuth } from '@/contexts/AuthContext'
import { queryKeys } from '@/lib/queryKeys'
import { searchableTypesFor, type SearchHit } from '@/lib/searchCategories'
import { estUnDelaiDeRecherche } from '@/components/search/searchPaletteHelpers'
import { chercherPartout, saisieCherchable, type RechercheGlobale } from '@/components/search/rechercheGlobale'

export function useLinkedEntitySearch(query: string, entityId: string | null) {
    const { hasPermission } = useAuth()

    const searchableTypes = useMemo(() => searchableTypesFor(hasPermission), [hasPermission])

    const q = query.trim()
    // Sous 3 caractères (sauf un identifiant), le serveur ne cherche rien (SEARCH-001 §7).
    const enabled = saisieCherchable(q) && searchableTypes.length > 0

    // Même appel que la palette (`chercherPartout`) : le cache est commun, la donnée
    // rangée sous cette clé a donc la même forme des deux côtés.
    const { data, isLoading, isError, error } = useQuery<RechercheGlobale>({
        queryKey: queryKeys.search.global(q, searchableTypes),
        queryFn: ({ signal }) => chercherPartout({ q, entityId, types: searchableTypes, signal }),
        enabled,
        placeholderData: keepPreviousData,
        retry: false,
        staleTime: 30_000,
    })

    const hits: SearchHit[] = useMemo(
        () =>
            (data?.results ?? []).map((h) => ({
                type: h.result_type,
                id: h.id,
                label: h.label,
                secondary: h.secondary,
                matchedField: h.matched_field,
                entityId: h.entity_id,
                organizationId: h.organization_id,
                originType: h.origin_type,
                originId: h.origin_id,
                avatarUrl: h.avatar_url,
            })),
        [data],
    )

    // Un refus (dont `search_timeout`, délai serveur dépassé) n'est pas « aucune fiche » :
    // le sélecteur le dit, sans quoi il ferait lire absente une fiche qui existe peut-être.
    const isTimeout = isError && estUnDelaiDeRecherche(error)
    // Correction des fautes abandonnée faute de temps : fiches trouvées SANS faute seulement.
    const typosSkipped = data?.typosSkipped === true

    return { hits, isLoading, isError, isTimeout, typosSkipped, enabled }
}
